import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from './authStore';
import type { 
  Room, 
  ChatMessage, 
  GameRound, 
  RoundResult, 
  GameResult,
  RoomPreview,
  RoomSettings,
  PlayerScore,
  ModeConfig,
  GameMode,
  Team,
  QuizQuestion
} from '@shared/types';

// Historique d'un round joué
export interface PlayedRound {
  round: number;
  title: string;
  artist: string;
  albumCover?: string;
}

// Statut des réponses des joueurs pour le round en cours
export interface PlayerAnswerStatus {
  playerId: string;
  playerName: string;
  titleFound: boolean;
  artistFound: boolean;
}

type RoomIntent = { event: string; data: unknown; expiresAt: number };
const INTENT_KEY = 'blindtest-entry-intent';
function isRetryableEntry(intent: RoomIntent): boolean {
  const data = intent.data as { code?: unknown; themeId?: unknown } | null;
  if (intent.event === 'room:join') return typeof data?.code === 'string' && /^[A-Z0-9]{4,6}$/.test(data.code);
  if (intent.event === 'autoroom:join') return typeof data?.themeId === 'string' && data.themeId.length > 0 && data.themeId.length <= 100;
  return false;
}
function rememberIntent(intent: RoomIntent | null) {
  try {
    if (intent && ['room:join', 'autoroom:join'].includes(intent.event)) sessionStorage.setItem(INTENT_KEY, JSON.stringify(intent));
    else sessionStorage.removeItem(INTENT_KEY);
  } catch { /* Private browsing may disable session storage; in-memory entry still works. */ }
}
function recallIntent(): RoomIntent | null {
  try {
    const intent = JSON.parse(sessionStorage.getItem(INTENT_KEY) || 'null');
    // Keep an expired destination available for an explicit retry after OAuth.
    // flushRoomIntent still rejects it before emitting anything.
    if (!intent || !Number.isFinite(intent.expiresAt) || intent.expiresAt <= 0 || intent.expiresAt > Date.now() + 120000) return null;
    if (intent.event === 'room:join' && typeof intent.data?.code === 'string' && /^[A-Z0-9]{4,6}$/.test(intent.data.code)) return intent;
    if (intent.event === 'autoroom:join' && typeof intent.data?.themeId === 'string' && intent.data.themeId.length > 0 && intent.data.themeId.length <= 100) return intent;
  } catch { /* Ignore unavailable or malformed session storage. */ }
  return null;
}
export interface AnswerFeedback { requestId: string; status: 'pending' | 'correct' | 'incorrect' | 'unconfirmed' }
export interface ResultSave { status: 'saved' | 'retrying' | 'error'; message?: string; xpGained?: number; newLevel?: number }
let unsubscribeAuth: (() => void) | undefined;
let answerTimer: ReturnType<typeof setTimeout> | undefined;
let sessionTimer: ReturnType<typeof setTimeout> | undefined;
let requestSequence = 0;
function identityKey(state: ReturnType<typeof useAuthStore.getState>): string | null {
  if (!state.isAuthenticated) return null;
  if (state.token && state.user) return `account:${state.user.id}:${state.token}`;
  if (state.player?.type === 'spotify' && state.spotifyToken) return `spotify:${state.spotifyToken}`;
  if (state.player?.type === 'guest') return `guest:${state.player.name}`;
  return null;
}

interface SocketState {
  socket: Socket | null;
  isConnected: boolean;
  isSocketAuthenticated: boolean;
  sessionError: string | null;
  isJoining: boolean;
  pendingIntent: RoomIntent | null;
  lastIntent: RoomIntent | null;
  retryJoin: () => void;
  answerFeedback: AnswerFeedback | null;
  resultSaves: Record<string, ResultSave>;
  resultWasGuest: boolean;
  resultPlayerId: string | null;
  joinAutoRoom: (themeId: string, discovery?: boolean) => void;
  queueRoom: (event: string, data: unknown) => void;
  flushRoomIntent: () => void;
  clearIntent: () => void;
  sendAnswer: (event: string, data: Record<string, string>) => void;
  
  // Room state
  currentRoom: Room | null;
  publicRooms: RoomPreview[];
  hasLeftRoom: boolean;  // Flag pour empêcher le rejoin auto
  
  // Game state
  isPlaying: boolean;
  isWaitingForPlayers: boolean;  // Attente avant le premier round
  currentRound: GameRound | null;
  roundResult: RoundResult | null;
  gameResult: GameResult | null;
  scores: PlayerScore[];
  myAnswer: { titleFound: boolean; artistFound: boolean } | null;
  playedRounds: PlayedRound[];  // Historique des chansons jouées
  playerAnswers: PlayerAnswerStatus[];  // Réponses des joueurs pour le round en cours
  
  // Game modes state
  gameMode: GameMode;
  modeConfig: ModeConfig | null;
  playerLives: Record<string, number>;  // Battle Royale
  eliminatedPlayers: string[];           // Battle Royale
  isEliminated: boolean;                 // Local player eliminated
  teams: Team[];                         // Mode Équipes
  teamScores: Record<string, number>;    // Mode Équipes
  currentQuestion: QuizQuestion | null;  // Mode Quiz
  quizAnswered: boolean;                 // Has answered quiz
  
  // Chat
  messages: ChatMessage[];
  
  // Actions
  connect: () => void;
  disconnect: () => void;
  
  // Player actions
  authAsGuest: (name: string) => void;
  authWithSpotify: (token: string) => void;
  
  // Room actions
  createRoom: (name: string, playlistId: string, settings?: Partial<RoomSettings>, isPrivate?: boolean) => void;
  joinRoom: (code: string) => void;
  leaveRoom: () => void;
  resetLeftRoom: () => void;
  kickPlayer: (playerId: string) => void;
  updatePlaylist: (playlistId: string, playlistName?: string) => void;
  fetchPublicRooms: () => void;
  
  // Game actions
  startGame: (tracks?: any[]) => void;
  submitAnswer: (answer: string) => void;
  submitQuizAnswer: (answerId: string) => void;  // Mode Quiz
  usePowerUp: (type: string, targetId?: string) => void;
  
  // Chat actions
  sendMessage: (message: string) => void;
  
  // Reset
  resetGame: () => void;
}

export const useSocketStore = create<SocketState>((set, get) => ({
  socket: null,
  isConnected: false,
  isSocketAuthenticated: false,
  sessionError: null,
  isJoining: false,
  pendingIntent: null,
  lastIntent: null,
  answerFeedback: null,
  resultSaves: {},
  resultWasGuest: false,
  resultPlayerId: null,
  
  currentRoom: null,
  publicRooms: [],
  hasLeftRoom: false,
  
  isPlaying: false,
  isWaitingForPlayers: false,
  currentRound: null,
  roundResult: null,
  playedRounds: [],
  gameResult: null,
  scores: [],
  myAnswer: null,
  playerAnswers: [],
  
  // Game modes initial state
  gameMode: 'classic',
  modeConfig: null,
  playerLives: {},
  eliminatedPlayers: [],
  isEliminated: false,
  teams: [],
  teamScores: {},
  currentQuestion: null,
  quizAnswered: false,
  
  messages: [],
  
  connect: () => {
    if (get().socket) return;
    const restored = get().pendingIntent || recallIntent();
    if (restored) set({ pendingIntent: restored, lastIntent: restored });
    const socket = io({ autoConnect: true, reconnection: true, reconnectionAttempts: 5, reconnectionDelay: 1000 });
    let requestedIdentity: string | null = null;
    const authenticate = () => {
      const state = useAuthStore.getState();
      const key = identityKey(state);
      if (!get().isConnected || !key || requestedIdentity === key) return;
      requestedIdentity = key;
      set({ isSocketAuthenticated: false, sessionError: null });
      clearTimeout(sessionTimer);
      sessionTimer = setTimeout(() => {
        if (!get().isSocketAuthenticated) {
          requestedIdentity = null;
          set({ sessionError: 'Connexion non confirmée. Réessayez.', isJoining: false });
        }
      }, 12000);
      if (state.token && state.user) socket.emit('player:auth', { type: 'registered', token: state.token });
      else if (state.player?.type === 'spotify' && state.spotifyToken) socket.emit('player:auth', { type: 'spotify', spotifyToken: state.spotifyToken });
      else if (state.player?.type === 'guest') socket.emit('player:auth', { type: 'guest', guestName: state.player.name });
    };
    socket.on('connect', () => {
      requestedIdentity = null;
      set({ isConnected: true, isSocketAuthenticated: false, sessionError: null });
      socket.emit('room:list');
      authenticate();
    });
    socket.on('disconnect', () => {
      clearTimeout(answerTimer); clearTimeout(sessionTimer);
      requestedIdentity = null;
      set(state => ({ isConnected: false, isSocketAuthenticated: false, isJoining: false,
        answerFeedback: state.answerFeedback?.status === 'pending' ? { ...state.answerFeedback, status: 'unconfirmed' } : state.answerFeedback }));
    });
    socket.on('connect_error', () => set({ sessionError: 'Serveur inaccessible. Nouvelle tentative en cours…' }));
    socket.on('player:authenticated', ({ player }) => {
      if (!requestedIdentity || requestedIdentity !== identityKey(useAuthStore.getState())) return;
      clearTimeout(sessionTimer);
      useAuthStore.getState().setPlayer(player);
      set({ isSocketAuthenticated: true, sessionError: null });
      get().flushRoomIntent();
    });
    let observedIdentity = identityKey(useAuthStore.getState());
    unsubscribeAuth = useAuthStore.subscribe((state) => {
      const nextIdentity = identityKey(state);
      if (nextIdentity === observedIdentity) return;
      observedIdentity = nextIdentity;
      if (requestedIdentity || get().isSocketAuthenticated || !identityKey(state)) {
        const intent = identityKey(state) || state.isLoading ? get().pendingIntent : null;
        if (!nextIdentity && !state.isLoading) rememberIntent(null);
        const preserveGuestResult = get().gameResult && get().resultWasGuest && state.player?.type !== 'guest' && nextIdentity;
        get().disconnect();
        if (!preserveGuestResult) get().resetGame();
        set({ currentRoom: null, messages: [], pendingIntent: intent, lastIntent: intent, resultSaves: {} });
        get().connect();
      } else authenticate();
    });

    socket.on('player:error', ({ message }) => {
      clearTimeout(sessionTimer); requestedIdentity = null;
      set({ isSocketAuthenticated: false, isJoining: false, sessionError: message });
    });
    
    // Room events
    socket.on('room:created', ({ room }) => {
      clearTimeout(sessionTimer);
      set({ currentRoom: room, hasLeftRoom: false, isJoining: false, sessionError: null, lastIntent: null });
    });
    
    socket.on('room:joined', ({ room }) => {
      clearTimeout(sessionTimer);
      set({ currentRoom: room, hasLeftRoom: false, isJoining: false, sessionError: null, lastIntent: null });
    });
    
    socket.on('room:left', () => {
      set({
        currentRoom: null,
        messages: [],
        hasLeftRoom: true,
        // Clear game state to prevent stale rejoin
        isPlaying: false,
        isWaitingForPlayers: false,
        currentRound: null,
        roundResult: null,
        gameResult: null,
        scores: [],
        myAnswer: null,
        playedRounds: [],
        playerAnswers: [],
      });
    });
    
    socket.on('room:kicked', () => {
      set({ currentRoom: null, messages: [] });
    });
    
    socket.on('room:playerJoined', ({ player }) => {
      set(state => {
        if (!state.currentRoom) return state;
        return {
          currentRoom: {
            ...state.currentRoom,
            players: [...state.currentRoom.players, player],
          },
        };
      });
    });
    
    socket.on('room:playerLeft', ({ playerId }) => {
      set(state => {
        if (!state.currentRoom) return state;
        return {
          currentRoom: {
            ...state.currentRoom,
            players: state.currentRoom.players.filter(p => p.id !== playerId),
          },
        };
      });
    });
    
    socket.on('room:list', ({ rooms }) => {
      set({ publicRooms: rooms });
    });
    
    socket.on('room:playlistUpdated', ({ playlistId, playlistName }: { playlistId: string; playlistName: string }) => {
      console.log('🎵 Playlist mise à jour:', playlistName);
      set(state => {
        if (!state.currentRoom) return state;
        return {
          currentRoom: {
            ...state.currentRoom,
            playlistId,
          },
        };
      });
    });
    
    socket.on('room:error', ({ message }) => {
      clearTimeout(sessionTimer); set({ isJoining: false, sessionError: message });
    });
    
    // Game events
    socket.on('game:started', ({ totalRounds: _totalRounds, settings: _settings, gameMode, modeConfig, teams, playerLives }) => {
      clearTimeout(answerTimer);
      set({ 
        currentRound: null, myAnswer: null, answerFeedback: null,
        isPlaying: true,
        isWaitingForPlayers: true,  // En attente du premier round
        gameResult: null,
        scores: [],
        playedRounds: [],  // Reset de l'historique
        playerAnswers: [],  // Reset des réponses
        // Mode state
        gameMode: gameMode || 'classic',
        modeConfig: modeConfig || null,
        teams: teams || [],
        teamScores: {},
        playerLives: playerLives || {},
        eliminatedPlayers: [],
        isEliminated: false,
        currentQuestion: null,
        quizAnswered: false,
      });
    });
    
    socket.on('game:roundStart', (round) => {
      clearTimeout(answerTimer);
      set({ answerFeedback: null,
        currentRound: round, 
        roundResult: null,
        myAnswer: null,
        isWaitingForPlayers: false,  // Plus en attente
        playerAnswers: [],  // Reset des réponses pour le nouveau round
        // Mode state
        currentQuestion: round.question || null,
        quizAnswered: false,
        playerLives: round.playerLives || {},
        eliminatedPlayers: round.eliminatedPlayers || [],
        teamScores: round.teamScores || {},
        teams: round.teams || [],
      });
    });
    
    // Un joueur a trouvé quelque chose
    socket.on('game:playerScored', (data: { playerId: string; playerName: string; titleFound: boolean; artistFound: boolean }) => {
      set(state => {
        // Trouver ou créer l'entrée pour ce joueur
        const existingIndex = state.playerAnswers.findIndex(p => p.playerId === data.playerId);
        if (existingIndex >= 0) {
          // Mettre à jour
          const newAnswers = [...state.playerAnswers];
          newAnswers[existingIndex] = {
            ...newAnswers[existingIndex],
            titleFound: data.titleFound,
            artistFound: data.artistFound,
          };
          return { playerAnswers: newAnswers };
        } else {
          // Ajouter
          return { 
            playerAnswers: [...state.playerAnswers, {
              playerId: data.playerId,
              playerName: data.playerName,
              titleFound: data.titleFound,
              artistFound: data.artistFound,
            }]
          };
        }
      });
    });
    
    socket.on('game:roundEnd', (result) => {
      clearTimeout(answerTimer);
      // Ajouter le round à l'historique
      const newPlayedRound: import('./socketStore').PlayedRound = {
        round: result.round,
        title: result.answer.title,
        artist: result.answer.artist,
        albumCover: result.answer.albumCover,
      };
      
      set(state => ({ 
        roundResult: result,
        answerFeedback: null,
        currentRound: null,
        scores: result.scores,
        playedRounds: [...state.playedRounds, newPlayedRound],
        // Update mode state from result
        playerLives: result.playerLives || state.playerLives,
        eliminatedPlayers: result.eliminatedPlayers || state.eliminatedPlayers,
        teamScores: result.teamScores || state.teamScores,
      }));
    });
    
    socket.on('game:ended', (result) => {
      clearTimeout(answerTimer);
      set({ answerFeedback: null, resultWasGuest: useAuthStore.getState().player?.type === 'guest', resultPlayerId: useAuthStore.getState().player?.id || null,
        gameResult: result, 
        isPlaying: false,
        currentRound: null,
        scores: result.scores,
        teams: result.teamRanking || [],
        teamScores: result.teamScores || {},
      });
    });
    
    // Battle Royale: joueur éliminé
    socket.on('game:eliminated', ({ reason }) => {
      console.log('💀 Vous êtes éliminé:', reason);
      set({ isEliminated: true });
    });
    
    socket.on('game:playerEliminated', ({ playerId, playerName, playersRemaining }) => {
      console.log(`💀 ${playerName} éliminé ! ${playersRemaining} joueurs restants`);
      set(state => ({
        eliminatedPlayers: [...state.eliminatedPlayers, playerId],
      }));
    });
    
    // Quiz mode
    socket.on('game:quizResult', ({ correct, pointsEarned, correctAnswerId: _correctId }) => {
      console.log('📝 Quiz result:', correct ? `+${pointsEarned}` : 'incorrect');
      set({ quizAnswered: true });
    });
    
    socket.on('game:playerAnsweredQuiz', ({ playerId: _playerId, playerName }) => {
      console.log(`📝 ${playerName} a répondu`);
    });
    
    socket.on('game:answerResult', (result) => {
      const state = get();
      if (!state.currentRound || !result.requestId || state.answerFeedback?.requestId !== result.requestId ||
          state.answerFeedback?.status !== 'pending') return;
      clearTimeout(answerTimer);
      const accepted = typeof result.titleFound === 'boolean' && typeof result.artistFound === 'boolean';
      set({ answerFeedback: { requestId: result.requestId, status: !accepted ? 'unconfirmed' : result.correct ? 'correct' : 'incorrect' },
        quizAnswered: state.gameMode === 'quiz' ? true : state.quizAnswered,
        myAnswer: { titleFound: !!(state.myAnswer?.titleFound || result.titleFound), artistFound: !!(state.myAnswer?.artistFound || result.artistFound) } });
    });
    socket.on('profile:updated', ({ gameId, saved, xpGained, newLevel }) => {
      if (saved !== true || !gameId) return;
      set(state => ({ resultSaves: { ...state.resultSaves, [gameId]: {
        status: 'saved',
        ...(typeof xpGained === 'number' && Number.isFinite(xpGained) && xpGained >= 0 ? { xpGained } : {}),
        ...(typeof newLevel === 'number' && Number.isFinite(newLevel) && newLevel >= 0 ? { newLevel } : {}),
      } } }));
    });
    socket.on('profile:saveError', ({ gameId, message, retryPending }) => {
      if (!gameId || get().resultSaves[gameId]?.status === 'saved') return;
      set(state => ({ resultSaves: { ...state.resultSaves, [gameId]: { status: retryPending ? 'retrying' : 'error', message } } }));
    });

    socket.on('game:error', ({ message }) => {
      console.error('❌ Erreur game:', message);
    });
    
    // Chat events
    socket.on('chat:message', (message) => {
      // Play notification sound if message is from someone else
      const currentPlayer = useAuthStore.getState().player;
      if (message.playerId && currentPlayer && message.playerId !== currentPlayer.id && !message.isSystem) {
        try {
          const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = 800;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0.1, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
          osc.start();
          osc.stop(ctx.currentTime + 0.3);
        } catch {}
      }
      set(state => ({
        messages: [...state.messages.slice(-99), message], // Garder 100 messages max
      }));
    });
    
    set({ socket });
  },
  
  disconnect: () => {
    unsubscribeAuth?.(); unsubscribeAuth = undefined;
    clearTimeout(answerTimer); clearTimeout(sessionTimer);
    const { socket } = get();
    socket?.removeAllListeners(); socket?.disconnect();
    set({ socket: null, isConnected: false, isSocketAuthenticated: false, isJoining: false, pendingIntent: null,
      answerFeedback: get().answerFeedback?.status === 'pending' ? { ...get().answerFeedback!, status: 'unconfirmed' } : get().answerFeedback });
  },
  authAsGuest: (name) => {
    const state = useAuthStore.getState();
    if (state.token || (state.isAuthenticated && state.player?.type !== 'guest')) return;
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 20) { set({ sessionError: 'Choisissez un pseudo de 2 à 20 caractères.' }); return; }
    // Retrying the same guest identity must also retry an unconfirmed authentication.
    if (state.player?.name === trimmed && state.isAuthenticated) {
      const intent = get().pendingIntent;
      get().disconnect(); set({ pendingIntent: intent }); get().connect();
    } else state.setPlayer({ id: '', name: trimmed, type: 'guest', avatar: null, level: 0, xp: 0, connectedAt: Date.now() });
  },
  authWithSpotify: (token) => {
    useAuthStore.setState({ spotifyToken: token });
  },
  clearIntent: () => { clearTimeout(sessionTimer); rememberIntent(null); set({ pendingIntent: null, lastIntent: null, isJoining: false, sessionError: null }); },
  retryJoin: () => {
    const intent = get().lastIntent;
    if (!intent) return;
    if (intent.expiresAt < Date.now() && !isRetryableEntry(intent)) return;
    const renewed = { ...intent, expiresAt: Date.now() + 120000 };
    if (!get().isSocketAuthenticated) {
      get().disconnect(); rememberIntent(renewed);
      set({ pendingIntent: renewed, lastIntent: renewed, sessionError: null }); get().connect();
    } else get().queueRoom(intent.event, intent.data);
  },
  queueRoom: (event, data) => {
    if (get().isJoining) return;
    const intent = { event, data, expiresAt: Date.now() + 120000 };
    rememberIntent(intent);
    set({ pendingIntent: intent, lastIntent: intent, sessionError: null });
    get().flushRoomIntent();
  },
  flushRoomIntent: () => {
    const { socket, pendingIntent, isSocketAuthenticated, isConnected } = get();
    if (!pendingIntent || !socket || !isConnected || !isSocketAuthenticated) return;
    rememberIntent(null);
    set({ pendingIntent: null }); // Consume before emitting: duplicate acknowledgements cannot replay it.
    if (pendingIntent.expiresAt < Date.now()) { set({ sessionError: 'Demande expirée. Réessayez.' }); return; }
    set({ isJoining: true }); socket.emit(pendingIntent.event, pendingIntent.data);
    clearTimeout(sessionTimer);
    sessionTimer = setTimeout(() => set({ isJoining: false, sessionError: 'Entrée dans la partie non confirmée. Réessayez.' }), 15000);
  },
  createRoom: (name, playlistId, settings, isPrivate) => get().queueRoom('room:create', { name, playlistId, settings, isPrivate }),
  joinRoom: (code) => get().queueRoom('room:join', { code }),
  joinAutoRoom: (themeId, discovery = false) => get().queueRoom('autoroom:join', { themeId, discovery }),

  leaveRoom: () => {
    const { socket } = get();
    if (socket) {
      socket.emit('room:leave');
    }
  },
  
  resetLeftRoom: () => {
    set({ hasLeftRoom: false });
  },
  
  kickPlayer: (playerId) => {
    const { socket } = get();
    if (socket) {
      socket.emit('room:kick', { playerId });
    }
  },
  
  updatePlaylist: (playlistId, playlistName) => {
    const { socket } = get();
    if (socket) {
      socket.emit('room:updatePlaylist', { playlistId, playlistName });
    }
  },
  
  fetchPublicRooms: () => {
    const { socket } = get();
    if (socket) {
      socket.emit('room:list');
    }
  },
  
  // Game actions
  startGame: (tracks) => {
    const { socket } = get();
    if (socket) {
      socket.emit('game:start', { tracks });
    }
  },
  
  sendAnswer: (event, data) => {
    const state = get();
    if (!state.socket || !state.isConnected || !state.isSocketAuthenticated || !state.currentRound || state.answerFeedback?.status === 'pending') return;
    const requestId = `${Date.now().toString(36)}-${++requestSequence}-${state.currentRound.round}`;
    clearTimeout(answerTimer);
    set({ answerFeedback: { requestId, status: 'pending' } });
    state.socket.emit(event, { ...data, requestId });
    answerTimer = setTimeout(() => {
      if (get().answerFeedback?.requestId === requestId) set({ answerFeedback: { requestId, status: 'unconfirmed' } });
    }, 10000);
  },
  submitAnswer: (answer) => {
    if (!answer.trim() || (get().myAnswer?.titleFound && get().myAnswer?.artistFound)) return;
    get().sendAnswer('game:answer', { answer: answer.trim() });
  },
  submitQuizAnswer: (answerId) => {
    const { socket } = get();
    if (socket) socket.emit('game:quizAnswer', { answerId });
  },

  usePowerUp: (type, targetId) => {
    const { socket } = get();
    if (socket) {
      socket.emit('game:usePowerUp', { type, targetId });
    }
  },
  
  // Chat actions
  sendMessage: (message) => {
    const { socket } = get();
    if (socket) {
      socket.emit('chat:message', { message });
    }
  },
  
  // Reset
  resetGame: () => {
    clearTimeout(answerTimer);
    set({
      isPlaying: false,
      isWaitingForPlayers: false,
      currentRound: null,
      roundResult: null,
      gameResult: null,
      scores: [],
      myAnswer: null,
      answerFeedback: null,
      playedRounds: [],
      // Reset mode state
      gameMode: 'classic',
      modeConfig: null,
      playerLives: {},
      eliminatedPlayers: [],
      isEliminated: false,
      teams: [],
      teamScores: {},
      currentQuestion: null,
      quizAnswered: false,
    });
  },
}));
